import { useState } from 'react';

import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import logo from '../../assets/images/logo.png';
import QR from '../../assets/images/QR.jpeg';
import HeaderComp from '../../components/elements/HeadingComponent';

const TopUpBalance = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [amount, setAmount] = useState('2000');

  // Generate random 6-digit number and store it in state to keep it consistent
  const [randomPaymentCode] = useState(() => {
    // Check if payment code already exists in localStorage
    const existingCode = localStorage.getItem('topUpPaymentCode');
    if (existingCode) {
      return parseInt(existingCode);
    }
    // Generate new code and store in localStorage
    const newCode = Math.floor(100000 + Math.random() * 900000);
    localStorage.setItem('topUpPaymentCode', newCode.toString());
    return newCode;
  });

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
    // Clear existing payment code when amount changes
    localStorage.removeItem('topUpPaymentCode');
  };

  return (
    <Box>
      <HeaderComp>Top Up Balance</HeaderComp>

      {/* Back Button */}
      <Box
        display="flex"
        alignItems="center"
        mb={4}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateX(-4px)',
          },
        }}
        onClick={handleBackClick}
      >
        <IconButton
          size="small"
          sx={{
            backgroundColor: 'rgba(18, 112, 220, 0.1)',
            color: theme.palette.primary.main,
            mr: 1,
            '&:hover': {
              backgroundColor: 'rgba(18, 112, 220, 0.2)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="primary"
          sx={{
            fontWeight: 500,
            userSelect: 'none',
          }}
        >
          Back
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="flex-start"
        flexDirection="row"
        alignItems="flex-start"
        gap={4}
      >
        {/* Left Side - Message */}
        <Box sx={{ flex: '0 0 400px' }}>
          <Card
            sx={{
              background: '#fbfcfc',
              border: '1px solid',
              borderColor: 'rgba(0, 128, 128, 0.06)',
              borderRadius: 1.5,
              boxShadow: 'none',
              height: 'fit-content',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'text.primary',
                }}
              >
                Account Top-Up Instructions
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.6,
                  mb: 3,
                }}
              >
                Please deposit the required amount using the QR code payment
                method. Your payment will be processed securely and the amount
                will be credited to your account balance within 24 hours.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontStyle: 'italic',
                  backgroundColor: 'rgba(66, 211, 146, 0.08)',
                  p: 2,
                  borderRadius: 1,
                  border: '1px solid rgba(66, 211, 146, 0.2)',
                }}
              >
                ⏱️ Processing Time: 24 hours
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Right Side - QR Code */}
        <Box
          sx={{
            flex: '0 0 400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box display="flex" alignItems="center" mb={3}>
            <img src={logo} alt="Tasalli Corporation" height={100} />
            <Typography variant="body2" fontWeight="bold">
              Tasalli Corporation
            </Typography>
          </Box>

          <Card
            sx={{
              position: 'relative',
              minHeight: '500px',
              background: '#fbfcfc',
              border: '1px solid',
              borderColor: 'rgba(0, 128, 128, 0.06)',
              borderRadius: 1.5,
              boxShadow: 'none',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                Enter{' '}
                <span
                  className="gradientText"
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    padding: '0 8px',
                    margin: '0 4px',
                  }}
                >
                  {randomPaymentCode}
                </span>{' '}
                as payment description
              </Typography>
              <img
                src={QR}
                height={400}
                width={330}
                alt="QR Code for Payment"
              />
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  color: 'text.secondary',
                  textAlign: 'center',
                }}
              >
                Scan the QR code to make payment
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default TopUpBalance;
